import React from 'react';
import PropTypes from 'prop-types';
import { ApartmentOutlined } from '@ant-design/icons';

// Timeline / detail-panel event glyphs.
//
// These are lucide icons, matching the design, NOT antd's icon font. The design's
// DOM for a node glyph is:
//   <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
//        stroke="currentColor" stroke-width="2" stroke-linecap="round"
//        stroke-linejoin="round" class="lucide lucide-shield text-destructive">
// antd's equivalents are a different family (heavier, partly filled), so they
// read visibly differently. Inline SVG also lets the glyph inherit `color` from
// the node circle through `stroke="currentColor"` — that is how the status hue
// reaches it.
//
// Sized 14x14 here rather than via CSS font-size: an <svg> does not respond to
// font-size the way an icon font does, and both consumers (EventNodeWrap and
// EventDetailPanel) want the same 14px.

const Glyph = ({ children }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

Glyph.propTypes = { children: PropTypes.node };

// lucide `monitor` — Login Attempt / Re-Login
const Desktop = () => (
  <Glyph>
    <rect width="20" height="14" x="2" y="3" rx="2" />
    <path d="M8 21h8" />
    <path d="M12 17v4" />
  </Glyph>
);

// lucide `shield` — path copied verbatim from the design's rendered DOM.
// Path data is not wrapped: a newline inside `d` is legal but makes the string
// hard to diff against the source icon, so max-len is waived instead.
const Shield = () => (
  <Glyph>
    {/* eslint-disable-next-line max-len */}
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
  </Glyph>
);

// lucide `triangle-alert` — Password Reset
const Warning = () => (
  <Glyph>
    {/* eslint-disable-next-line max-len */}
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </Glyph>
);

// lucide `user-check` — Beneficiary Added
const UserAdd = () => (
  <Glyph>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="m16 11 2 2 4-4" />
  </Glyph>
);

// lucide `dollar-sign`. Superseded by Landmark for Fund Transfer (2026-08-23) but
// kept: the mock fixture still names it, and nothing else supplies a money glyph.
const Dollar = () => (
  <Glyph>
    <path d="M12 2v20" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </Glyph>
);

// ── The design's actual node glyphs (2026-08-23) ───────────────────────────────
// Login/Re-Login, the OTP burst, the two password events and Fund Transfer were
// rendering monitor / shield / triangle-alert / dollar-sign; the design uses
// log-in, message-square, key and landmark. Remapped in toEvents.js.
//
// PROVENANCE DIFFERS from Shield above, whose path was copied verbatim out of the
// design's DOM. These four are transcribed from the lucide icon set by name — the
// names are certain (the design's classes read `lucide lucide-log-in` etc.), the
// path data is not copied from a capture. A wrong curve would be visible at a
// glance, but re-check against the DOM if anything looks off.

// lucide `log-in` — Login Attempt / Re-Login
const LogIn = () => (
  <Glyph>
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <polyline points="10 17 15 12 10 7" />
    <line x1="15" x2="3" y1="12" y2="12" />
  </Glyph>
);

// lucide `message-square` — Failed OTP Attempts
const MessageSquare = () => (
  <Glyph>
    {/* eslint-disable-next-line max-len */}
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </Glyph>
);

// lucide `key-round` — Password Reset / Password Changed.
// SWITCHED from lucide `key` on 2026-08-23: `key` draws a large ring at the
// lower-left with a long shaft to the top-right corner, and that did not match
// the design. `key-round` is the other plausible variant — a rounded head with a
// notched tail. If this is still wrong the answer is the icon's class name in the
// design's DOM (`lucide-key` / `lucide-key-round` / `lucide-key-square`); the
// three are not distinguishable from a 14px screenshot.
const Key = () => (
  <Glyph>
    {/* eslint-disable-next-line max-len */}
    <path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" />
    <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
  </Glyph>
);

// lucide `landmark` — Fund Transfer (a bank, not a dollar sign)
const Landmark = () => (
  <Glyph>
    <line x1="3" x2="21" y1="22" y2="22" />
    <line x1="6" x2="6" y1="18" y2="11" />
    <line x1="10" x2="10" y1="18" y2="11" />
    <line x1="14" x2="14" y1="18" y2="11" />
    <line x1="18" x2="18" y1="18" y2="11" />
    <polygon points="12 2 20 7 4 7" />
  </Glyph>
);

// ── The rest of the standard lucide set the picker offers ─────────────────────
// The design's icon picker (fincense-labs-2, /framl/settings) captions itself
// "Standard Lucide icon set (lucide.dev)" and lays out THIRTY-SIX glyphs in six
// rows of six, in ALPHABETICAL order. Seven of them already existed above; these
// are the other twenty-nine, added in that same order.
//
// PROVENANCE, as for the four block above: the NAMES are read off the design's
// grid and cross-checked against the alphabetical ordering (each glyph has to
// sort between its neighbours, which is what makes the reading more than a
// guess). The PATH DATA is transcribed from the lucide set by name, not copied
// out of a capture. A wrong curve shows up at a glance; re-check against
// lucide.dev if one looks off.

const ArrowLeftRight = () => (
  <Glyph>
    <path d="M8 3 4 7l4 4" />
    <path d="M4 7h16" />
    <path d="m16 21 4-4-4-4" />
    <path d="M20 17H4" />
  </Glyph>
);

const Banknote = () => (
  <Glyph>
    <rect width="20" height="12" x="2" y="6" rx="2" />
    <circle cx="12" cy="12" r="2" />
    <path d="M6 12h.01M18 12h.01" />
  </Glyph>
);

const Building = () => (
  <Glyph>
    <rect width="16" height="20" x="4" y="2" rx="2" ry="2" />
    <path d="M9 22v-4h6v4" />
    <path d="M8 6h.01" />
    <path d="M16 6h.01" />
    <path d="M12 6h.01" />
    <path d="M12 10h.01" />
    <path d="M12 14h.01" />
    <path d="M16 10h.01" />
    <path d="M16 14h.01" />
    <path d="M8 10h.01" />
    <path d="M8 14h.01" />
  </Glyph>
);

const Coins = () => (
  <Glyph>
    <circle cx="8" cy="8" r="6" />
    <path d="M18.09 10.37A6 6 0 1 1 10.34 18" />
    <path d="M7 6h1v4" />
    <path d="m16.71 13.88.7.71-2.82 2.82" />
  </Glyph>
);

const CreditCard = () => (
  <Glyph>
    <rect width="20" height="14" x="2" y="5" rx="2" />
    <line x1="2" x2="22" y1="10" y2="10" />
  </Glyph>
);

const Fingerprint = () => (
  <Glyph>
    <path d="M2 12C2 6.5 6.5 2 12 2a10 10 0 0 1 8 4" />
    <path d="M5 19.5C5.5 18 6 15 6 12c0-.7.12-1.37.34-2" />
    <path d="M17.29 21.02c.12-.6.43-2.3.5-3.02" />
    <path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4" />
    <path d="M8.65 22c.21-.66.45-1.32.57-2" />
    <path d="M14 13.12c0 2.38 0 6.38-1 8.88" />
    <path d="M2 16h.01" />
    <path d="M21.8 16c.2-2 .131-5.354 0-6" />
    <path d="M9 6.8a6 6 0 0 1 9 5.2c0 .47 0 1.17-.02 2" />
  </Glyph>
);

const Gift = () => (
  <Glyph>
    <rect x="3" y="8" width="18" height="4" rx="1" />
    <path d="M12 8v13" />
    <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
    {/* eslint-disable-next-line max-len */}
    <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5" />
  </Glyph>
);

const Globe = () => (
  <Glyph>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
    <path d="M2 12h20" />
  </Glyph>
);

// `hand-coins`, NOT `handshake`. I read this one off the rendered grid and
// guessed wrong; the design's DOM names it outright — `<button
// title="HandCoins" …>` — and the title attributes are lucide's own component
// names, so they are authoritative where a 16px swatch is not.
const HandCoins = () => (
  <Glyph>
    <path d="M11 15h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 17" />
    {/* eslint-disable-next-line max-len */}
    <path d="m7 21 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9" />
    <path d="m2 16 6 6" />
    <circle cx="16" cy="9" r="2.9" />
    <circle cx="6" cy="5" r="3" />
  </Glyph>
);

const Laptop = () => (
  <Glyph>
    {/* eslint-disable-next-line max-len */}
    <path d="M20 16V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v9m16 0H4m16 0 1.28 2.55a1 1 0 0 1-.9 1.45H3.62a1 1 0 0 1-.9-1.45L4 16" />
  </Glyph>
);

const LogOut = () => (
  <Glyph>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" x2="9" y1="12" y2="12" />
  </Glyph>
);

const Mail = () => (
  <Glyph>
    <rect width="20" height="16" x="2" y="4" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </Glyph>
);

const MapPin = () => (
  <Glyph>
    {/* eslint-disable-next-line max-len */}
    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
    <circle cx="12" cy="10" r="3" />
  </Glyph>
);

const PiggyBank = () => (
  <Glyph>
    {/* eslint-disable-next-line max-len */}
    <path d="M19 5c-1.5 0-2.8 1.4-3 2-3.5-1.5-11-.3-11 5 0 1.8 0 3 2 4.5V20h4v-2h3v2h4v-4c1-.5 1.7-1 2-2h2v-4h-2c0-1-.5-1.5-1-2V5z" />
    <path d="M2 9v1c0 1.1.9 2 2 2h1" />
    <path d="M16 11h.01" />
  </Glyph>
);

const QrCode = () => (
  <Glyph>
    <rect width="5" height="5" x="3" y="3" rx="1" />
    <rect width="5" height="5" x="16" y="3" rx="1" />
    <rect width="5" height="5" x="3" y="16" rx="1" />
    <path d="M21 16h-3a2 2 0 0 0-2 2v3" />
    <path d="M21 21v.01" />
    <path d="M12 7v3a2 2 0 0 1-2 2H7" />
    <path d="M3 12h.01" />
    <path d="M12 3h.01" />
    <path d="M12 16v.01" />
    <path d="M16 12h1" />
    <path d="M21 12v.01" />
    <path d="M12 21v-1" />
  </Glyph>
);

const Receipt = () => (
  <Glyph>
    {/* eslint-disable-next-line max-len */}
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
    <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
    <path d="M12 17.5v-11" />
  </Glyph>
);

const RefreshCw = () => (
  <Glyph>
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
    <path d="M8 16H3v5" />
  </Glyph>
);

const Repeat = () => (
  <Glyph>
    <path d="m17 2 4 4-4 4" />
    <path d="M3 11v-1a4 4 0 0 1 4-4h14" />
    <path d="m7 22-4-4 4-4" />
    <path d="M21 13v1a4 4 0 0 1-4 4H3" />
  </Glyph>
);

const Send = () => (
  <Glyph>
    {/* eslint-disable-next-line max-len */}
    <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
    <path d="m21.854 2.147-10.94 10.939" />
  </Glyph>
);

// shield-alert / shield-check reuse the SHIELD OUTLINE copied verbatim from the
// design's DOM (see Shield above), so all three agree by construction.
const SHIELD_OUTLINE =
  // eslint-disable-next-line max-len
  'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z';

const ShieldAlert = () => (
  <Glyph>
    <path d={SHIELD_OUTLINE} />
    <path d="M12 8v4" />
    <path d="M12 16h.01" />
  </Glyph>
);

const ShieldCheck = () => (
  <Glyph>
    <path d={SHIELD_OUTLINE} />
    <path d="m9 12 2 2 4-4" />
  </Glyph>
);

const ShoppingBag = () => (
  <Glyph>
    <path d="M16 10a4 4 0 0 1-8 0" />
    <path d="M3.103 6.034h17.794" />
    {/* eslint-disable-next-line max-len */}
    <path d="M3.4 5.467a2 2 0 0 0-.4 1.2V20a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.667a2 2 0 0 0-.4-1.2l-2-2.667A2 2 0 0 0 17 2H7a2 2 0 0 0-1.6.8z" />
  </Glyph>
);

const Smartphone = () => (
  <Glyph>
    <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
    <path d="M12 18h.01" />
  </Glyph>
);

const Store = () => (
  <Glyph>
    <path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" />
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
    <path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4" />
    <path d="M2 7h20" />
    {/* eslint-disable-next-line max-len */}
    <path d="M22 7v3a2 2 0 0 1-2 2a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12a2 2 0 0 1-2-2V7" />
  </Glyph>
);

const Ticket = () => (
  <Glyph>
    {/* eslint-disable-next-line max-len */}
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
    <path d="M13 5v2" />
    <path d="M13 17v2" />
    <path d="M13 11v2" />
  </Glyph>
);

const Truck = () => (
  <Glyph>
    <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
    <path d="M15 18H9" />
    {/* eslint-disable-next-line max-len */}
    <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
    <circle cx="17" cy="18" r="2" />
    <circle cx="7" cy="18" r="2" />
  </Glyph>
);

const UserPlus = () => (
  <Glyph>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="19" x2="19" y1="8" y2="14" />
    <line x1="22" x2="16" y1="11" y2="11" />
  </Glyph>
);

const Users = () => (
  <Glyph>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </Glyph>
);

const Wallet = () => (
  <Glyph>
    {/* eslint-disable-next-line max-len */}
    <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" />
    <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />
  </Glyph>
);

// Maps an `icon_key` to its glyph. Shared by the timeline node, the detail-panel
// header and the settings picker so the three never drift.
//
// KEYED BY LUCIDE'S OWN NAMES (kebab-case), not by the ad-hoc names this map
// started with. The picker offers the standard set and captions itself with
// lucide.dev, so any other key space would have to be translated on the way in
// and on the way out — and a reader comparing a stored `desktop` against a grid
// labelled `Monitor` has no way to tell they are the same glyph.
//
// LARGER THAN WHAT THE PICKER OFFERS, deliberately: `shield` and `dollar-sign`
// are not in the design's 36 but ARE in use (`shield` is stored on a row today,
// `dollar-sign` is named by the timeline's mock fixture). Dropping them would
// blank a glyph that renders now, so they stay resolvable while staying out of
// the grid. Registry ⊇ picker set is the invariant.
export const EVENT_ICONS = {
  'alert-triangle': Warning,
  'arrow-left-right': ArrowLeftRight,
  banknote: Banknote,
  building: Building,
  coins: Coins,
  'credit-card': CreditCard,
  fingerprint: Fingerprint,
  gift: Gift,
  globe: Globe,
  'hand-coins': HandCoins,
  'key-round': Key,
  landmark: Landmark,
  laptop: Laptop,
  'log-in': LogIn,
  'log-out': LogOut,
  mail: Mail,
  'map-pin': MapPin,
  'message-square': MessageSquare,
  monitor: Desktop,
  'piggy-bank': PiggyBank,
  'qr-code': QrCode,
  receipt: Receipt,
  'refresh-cw': RefreshCw,
  repeat: Repeat,
  send: Send,
  'shield-alert': ShieldAlert,
  'shield-check': ShieldCheck,
  'shopping-bag': ShoppingBag,
  smartphone: Smartphone,
  store: Store,
  ticket: Ticket,
  truck: Truck,
  'user-check': UserAdd,
  'user-plus': UserPlus,
  users: Users,
  wallet: Wallet,

  // ── Resolvable but NOT offered in the picker; see above. ──
  shield: Shield,
  'dollar-sign': Dollar
};

/**
 * The thirty-six the PICKER offers, in the design's order.
 *
 * Six rows of six, alphabetical. Derived from EVENT_ICONS rather than retyped so
 * the two cannot disagree about which keys exist — `EXCLUDED` is the only list
 * that has to be maintained, and it is the short one.
 */
const EXCLUDED_FROM_PICKER = new Set(['shield', 'dollar-sign']);

export const PICKER_ICON_KEYS = Object.keys(EVENT_ICONS)
  .filter((k) => !EXCLUDED_FROM_PICKER.has(k))
  .sort();

/**
 * OLD KEY -> LUCIDE KEY.
 *
 * `icon_key` IS PERSISTED, so rekeying the registry is a data migration, not a
 * rename. `fp_event_type_config` already holds `desktop`, `message`, `shield` and
 * `landmark`; the timeline's `ICON_BY_EVENT_TYPE` and its mock fixture name
 * `login`, `warning`, `userAdd`, `key` and `dollar`. Every one of those would
 * resolve to nothing under the new keys, blanking icons an admin had chosen.
 *
 * Read-side only, and on purpose: nothing writes an old key any more, so this
 * shrinks to nothing as rows are re-saved. A backfill UPDATE would let it be
 * deleted outright — worth doing, but it needs to run on every deployment that
 * has stored one, so it is Jayaraghavan's migration to own, not ours to assume.
 */
export const ICON_ALIASES = {
  desktop: 'monitor',
  warning: 'alert-triangle',
  userAdd: 'user-check',
  login: 'log-in',
  message: 'message-square',
  key: 'key-round',
  dollar: 'dollar-sign'
  // `shield` and `landmark` need no entry — both are real keys in the new space.
};

/** Applies the legacy aliases. The one place old keys are understood. */
export const canonicalIconKey = (key) =>
  (key && ICON_ALIASES[key]) || key || undefined;

export const getEventIcon = (key) =>
  EVENT_ICONS[canonicalIconKey(key)] || ApartmentOutlined;

/**
 * The DEFAULT glyph for an event type that has no configured `icon_key`.
 *
 * Lives here rather than in toEvents.js (where it started) because two surfaces
 * need it and they must not drift: the timeline node, and the Event Fields
 * settings list — where every row was otherwise drawing the same generic
 * fallback while the design shows a distinct glyph per type.
 *
 * A type absent from this map has NO default. The settings list renders no icon
 * for it, matching the design (its "Beneficiary Update" row has none), and the
 * timeline falls back to the category glyph.
 */
export const ICON_BY_EVENT_TYPE = {
  // ── verified against live data ──
  LOGIN: 'log-in',
  LOGIN_MFA_WITH_OTP: 'message-square',
  ACCOUNT_PASSWORD_RESET: 'key-round',
  PASSWORD_CHANGED: 'key-round',
  // Not in the design — it renders no settings event — so this is a judgement
  // call, not a measurement: a transaction-limit change is closest to the
  // existing "something about this account changed" glyph.
  MODIFY_TRANSACTION_SETTINGS: 'alert-triangle',

  // ── unverified, never observed on the wire ──
  // CHANGED GLYPH (2026-09-03): this drew `login` — an arrow pointing IN — only
  // because the nine-glyph registry had no log-out. It does now.
  LOGOUT: 'log-out',
  OTP_VERIFICATION: 'message-square',
  PASSWORD_RESET: 'key-round',
  PASSWORD_CHANGE: 'key-round',
  PROFILE_UPDATE: 'alert-triangle',
  ACCOUNT_LINKING: 'user-check',
  // `user-check`, NOT `user-plus`, even though the old key was named `userAdd`:
  // the component that key pointed at drew lucide's user-CHECK, so this keeps
  // the rendering byte-identical through the rekey. Now that both glyphs are in
  // the registry, which one "Beneficiary Added" should draw is a real question
  // — raised for Yogesh rather than decided here.
  BENEFICIARY_ADDED: 'user-check'
};

export default EVENT_ICONS;
